import React, { useEffect, useRef, useState } from 'react';
import Hls from 'hls.js';
import { DEVICE_ENDPOINT } from '../../services/Devices';
import { getToken, getUserId } from '../../services/DefaultService';
import { Box, CircularProgress, Typography } from '@mui/material';
import './DeviceReverseConnectionStream.css';
import DeviceConnectionStreamContainerComponent from '../DeviceConnectionStreamContainerComponent';
import logger from '../../utils/logger';

interface DeviceStreamComponentProps {
  deviceId?: number;
  token?: string;
  isEnableVideo?: boolean;
}

const DeviceReverseConnectionStreamComponent = ({
  deviceId,
  token,
  isEnableVideo,
}: DeviceStreamComponentProps) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isError, setIsError] = useState(false);

  useEffect(() => {
    const token = getToken();
    let ws: WebSocket;

    if (token != null) {
      ws = new WebSocket(
        // @ts-ignore
        window._env_.REACT_APP_API_WS_HOST +
          `/${DEVICE_ENDPOINT}/${deviceId}/realPlay/stream?token=Bearer ` +
          atob(token),
      );
      ws.addEventListener('message', () => {});
    }
    return () => {
      ws?.close();
    };
  }, [deviceId]);

  const fetchWithRetry = async (
    url: string,
    options: RequestInit,
    maxRetries = 7,
    retryDelay = 1000,
  ): Promise<Response> => {
    for (let i = 0; i < maxRetries; i++) {
      try {
        const response = await fetch(url, options);
        if (response.ok) return response;
        throw new Error(`HTTP error: ${response.status}`);
      } catch (error) {
        if (i === maxRetries - 1) throw error;
        await new Promise((resolve) =>
          setTimeout(resolve, retryDelay * (i + 1)),
        );
      }
    }
    throw new Error('Max retries reached');
  };

  useEffect(() => {
    const video = videoRef.current;
    if (!video || !deviceId) return;

    const userId = getUserId();
    if (!userId) return;

    // @ts-ignore
    const baseUrl = window._env_.REACT_APP_API_URL_HOST;
    const playlistUrl = `${baseUrl}/live/${deviceId}/index.m3u8?t=${Date.now()}`;
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);

    if (!isIOS && Hls.isSupported()) {
      const hls = new Hls({
        lowLatencyMode: true,
        maxBufferLength: 1,
        maxMaxBufferLength: 1,
        maxBufferSize: 0,
        maxBufferHole: 0.1,
        maxSeekHole: 0.1,
        backBufferLength: 0,
        enableWorker: false,
        enableSoftwareAES: true,
        startLevel: -1,
        abrEwmaDefaultEstimate: 500000,
        abrBandWidthFactor: 0.8,
        abrBandWidthUpFactor: 0.7,
        abrMaxWithRealBitrate: true,
        startFragPrefetch: true,
        fpsDroppedMonitoringPeriod: 500,
        fpsDroppedMonitoringThreshold: 0.2,
        capLevelToPlayerSize: true,
        // @ts-ignore
        xhrSetup: (xhr) => {
          xhr.setRequestHeader('Authorization', `Bearer ${token}`);
        },
      });

      //@ts-ignore
      hls.on(Hls.Events.MANIFEST_PARSED, (event, data) => {
        video.currentTime = video.duration - 1;
      });
      const maxRetries = 7;
      let retryCount = 0;
      const retryDelay = 2000;

      const loadSourceWithRetry = () => {
        hls.loadSource(playlistUrl);
        hls.attachMedia(video);
      };

      // @ts-ignore
      hls.on(Hls.Events.ERROR, (_, data) => {
        if (data.fatal) {
          if (retryCount < maxRetries) {
            retryCount++;
            logger.debug(`Retrying (${retryCount}/${maxRetries})...`);
            setTimeout(loadSourceWithRetry, retryDelay);
          } else {
            setIsError(true);
            logger.error('Max retries reached. Stopping.');
          }
        }
      });

      //@ts-ignore
      hls.on(Hls.Events.BUFFER_CREATED, (_, data) => {
        setIsLoading(false);
      });

      loadSourceWithRetry();

      return () => hls.destroy();
    } else {
      const maxRetries = 7;
      const retryCount = 0;
      const retryDelay = 2000;

      const loadWithRetry = async () => {
        try {
          const response = await fetchWithRetry(
            playlistUrl,
            { headers: { Authorization: `Bearer ${token}` } },
            maxRetries,
            retryDelay,
          );
          const m3u8 = await response.text();
          const modifiedM3u8 = m3u8.replace(
            /(.*\.(m3u8|mp4|ts))/g,
            `$1?token=Bearer_${token}`,
          );
          video.src = URL.createObjectURL(new Blob([modifiedM3u8]));
        } catch (error) {
          logger.error('Failed to load HLS stream after retries:', error);
        }
      };

      loadWithRetry();
    }
  }, [deviceId, token]);

  useEffect(() => {
    const video = videoRef.current;

    if (video) {
      let allowPlay = true;

      const videoEl = video as any;

      const handleSeeking = () => {
        if (videoEl.oldTime !== undefined) {
          video.currentTime = videoEl.oldTime;
        }
      };

      const handleTimeUpdate = () => {
        videoEl.oldTime = video.currentTime;
      };

      const handlePause = async () => {
        if (allowPlay) {
          try {
            allowPlay = false;
            await video.play();
          } catch (err) {
            logger.error('Erro ao tentar retomar vídeo:', err);
          } finally {
            allowPlay = true;
          }
        }
      };

      video.addEventListener('seeking', handleSeeking);
      video.addEventListener('timeupdate', handleTimeUpdate);
      video.addEventListener('pause', handlePause);

      return () => {
        video.removeEventListener('seeking', handleSeeking);
        video.removeEventListener('timeupdate', handleTimeUpdate);
        video.removeEventListener('pause', handlePause);
      };
    }
  }, []);
  return (
    <DeviceConnectionStreamContainerComponent
      isEnableVideo={isEnableVideo}
      handleVideo={
        <video
          ref={videoRef}
          autoPlay
          controls
          controlsList="nodownload noremoteplayback"
          playsInline
          style={{
            width: '100%',
            display: !isLoading ? 'flex' : 'none',
            backgroundColor: '#000',
          }}
        />
      }
      isLoading={isLoading}
      isError={isError}
    />
  );
};

export default DeviceReverseConnectionStreamComponent;
